// T105: Create comment service for dsteem SDK
// T108: Add comment via dsteem SDK

import { PrivateKey, type Operation } from '@steempro/dsteem'
import { getSteemitClient } from './client'
import { generateCommentPermlink } from '@/utils/permlink'

const APP_NAME = 'fast-steemit/0.1.0'

export interface CreateCommentParams {
  author: string
  postingKey: string
  parentAuthor: string
  parentPermlink: string
  body: string
}

export interface UpdateCommentParams {
  author: string
  postingKey: string
  permlink: string
  parentAuthor: string
  parentPermlink: string
  body: string
}

export interface DeleteCommentParams {
  author: string
  postingKey: string
  permlink: string
}

export interface CommentResult {
  success: boolean
  permlink?: string
  error?: string
}

/**
 * Creates a new comment on Steemit blockchain
 */
export async function createComment(params: CreateCommentParams): Promise<CommentResult> {
  try {
    const { author, postingKey, parentAuthor, parentPermlink, body } = params

    // T113: Validate comment content
    if (!body || body.trim().length === 0) {
      return {
        success: false,
        error: '댓글 내용을 입력해주세요',
      }
    }

    if (body.length > 65535) {
      return {
        success: false,
        error: '댓글이 너무 깁니다 (최대 65535자)',
      }
    }

    // T107: Generate unique comment permlink
    const permlink = generateCommentPermlink(parentAuthor, parentPermlink)

    // Prepare metadata
    const metadata = {
      app: APP_NAME,
      format: 'markdown',
    }

    // Create the comment operation
    const operation = [
      'comment',
      {
        parent_author: parentAuthor,
        parent_permlink: parentPermlink,
        author,
        permlink,
        title: '', // Comments don't have titles
        body,
        json_metadata: JSON.stringify(metadata),
      },
    ]

    // Broadcast to blockchain
    const client = getSteemitClient()
    const privateKey = PrivateKey.fromString(postingKey)

    const result = await client.broadcast.sendOperations(
      [operation as unknown as Operation],
      privateKey
    )

    if (result) {
      return {
        success: true,
        permlink,
      }
    }

    return {
      success: false,
      error: '댓글 작성에 실패했습니다',
    }
  } catch (error) {
    console.error('Error creating comment:', error)

    // T114: Handle comment submission errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'

    // Parse common error messages
    if (errorMessage.includes('bandwidth')) {
      return {
        success: false,
        error: 'RC(리소스 크레딧)가 부족합니다. 잠시 후 다시 시도해주세요.',
      }
    }

    if (errorMessage.includes('You may only comment once every')) {
      return {
        success: false,
        error: '댓글을 너무 자주 작성하고 있습니다. 잠시 후 다시 시도해주세요.',
      }
    }

    return {
      success: false,
      error: errorMessage,
    }
  }
}

/**
 * Updates an existing comment on Steemit blockchain
 */
export async function updateComment(params: UpdateCommentParams): Promise<CommentResult> {
  try {
    const { author, postingKey, permlink, parentAuthor, parentPermlink, body } = params

    // Validate content
    if (!body || body.trim().length === 0) {
      return {
        success: false,
        error: '댓글 내용을 입력해주세요',
      }
    }

    // Check if comment can be edited
    const client = getSteemitClient()
    const comment = await client.database.call('get_content', [author, permlink])

    if (!comment || !comment.author) {
      return {
        success: false,
        error: '댓글을 찾을 수 없습니다',
      }
    }

    const created = new Date(comment.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        success: false,
        error: '7일이 지난 댓글은 수정할 수 없습니다',
      }
    }

    // Prepare metadata
    const metadata = {
      app: APP_NAME,
      format: 'markdown',
    }

    // Create the update operation
    const operation = [
      'comment',
      {
        parent_author: parentAuthor,
        parent_permlink: parentPermlink,
        author,
        permlink,
        title: '',
        body,
        json_metadata: JSON.stringify(metadata),
      },
    ]

    // Broadcast to blockchain
    const privateKey = PrivateKey.fromString(postingKey)
    const result = await client.broadcast.sendOperations(
      [operation as unknown as Operation],
      privateKey
    )

    if (result) {
      return {
        success: true,
        permlink,
      }
    }

    return {
      success: false,
      error: '댓글 수정에 실패했습니다',
    }
  } catch (error) {
    console.error('Error updating comment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Deletes a comment from Steemit blockchain
 * Only works if comment has no replies and is within 7 days
 */
export async function deleteComment(params: DeleteCommentParams): Promise<CommentResult> {
  try {
    const { author, postingKey, permlink } = params

    // Check delete constraints
    const client = getSteemitClient()
    const comment = await client.database.call('get_content', [author, permlink])

    if (!comment || !comment.author) {
      return {
        success: false,
        error: '댓글을 찾을 수 없습니다',
      }
    }

    // Check if comment has replies
    if (comment.children > 0) {
      return {
        success: false,
        error: '답글이 있는 댓글은 삭제할 수 없습니다',
      }
    }

    // Check 7-day window
    const created = new Date(comment.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        success: false,
        error: '7일이 지난 댓글은 삭제할 수 없습니다',
      }
    }

    // Create the delete operation
    const operation = [
      'delete_comment',
      {
        author,
        permlink,
      },
    ]

    // Broadcast to blockchain
    const privateKey = PrivateKey.fromString(postingKey)
    const result = await client.broadcast.sendOperations(
      [operation as unknown as Operation],
      privateKey
    )

    if (result) {
      return { success: true }
    }

    return {
      success: false,
      error: '댓글 삭제에 실패했습니다',
    }
  } catch (error) {
    console.error('Error deleting comment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Checks if a comment can be edited or deleted
 */
export async function canModifyComment(author: string, permlink: string): Promise<{
  canEdit: boolean
  canDelete: boolean
  reason?: string
}> {
  try {
    const client = getSteemitClient()
    const comment = await client.database.call('get_content', [author, permlink])

    if (!comment || !comment.author) {
      return {
        canEdit: false,
        canDelete: false,
        reason: '댓글을 찾을 수 없습니다',
      }
    }

    // Check 7-day window
    const created = new Date(comment.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        canEdit: false,
        canDelete: false,
        reason: '7일이 지난 댓글은 수정/삭제할 수 없습니다',
      }
    }

    // Check if comment has replies (affects delete only)
    const hasReplies = comment.children > 0

    return {
      canEdit: true,
      canDelete: !hasReplies,
      reason: hasReplies ? '답글이 있는 댓글은 삭제할 수 없습니다' : undefined,
    }
  } catch {
    return {
      canEdit: false,
      canDelete: false,
      reason: '댓글 확인 중 오류가 발생했습니다',
    }
  }
}
